// =====================================================================
// V3 per-student pricing helpers.
//
// Each student carries (all optional / additive):
//   * price_per_session         — USD charged to the family per session
//   * insurance_coverage_percent — % of that price insurance reimburses (reporting only)
//   * session_length_minutes    — length of one session (default 60)
//
// These power the Top-Up page's automatic minute calculation, where the admin
// types EITHER a number of sessions OR a dollar amount and the rest is derived.
// Insurance is intentionally kept OUT of the top-up math (top-up amount = full
// price × sessions); it only surfaces on the student/session reporting views.
// =====================================================================

export const DEFAULT_SESSION_LENGTH = 60

/** Length of one session in minutes, falling back to the 60-minute default. */
export function getSessionLength(sessionLengthMinutes: number | null | undefined): number {
  if (sessionLengthMinutes && sessionLengthMinutes > 0) return sessionLengthMinutes
  return DEFAULT_SESSION_LENGTH
}

/** Format a USD amount, or an em dash when unset. */
export function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || isNaN(amount)) return '—'
  return `$${Number(amount).toFixed(2)}`
}

/** Format an insurance percentage, or an em dash when unset. */
export function formatPercent(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || isNaN(pct)) return '—'
  return `${Number(pct).toFixed(pct % 1 === 0 ? 0 : 1)}%`
}

/** Out-of-pocket portion the family actually pays after insurance (reporting). */
export function outOfPocket(
  pricePerSession: number | null | undefined,
  insurancePercent: number | null | undefined
): number | null {
  if (pricePerSession === null || pricePerSession === undefined) return null
  const pct = Math.min(Math.max(insurancePercent ?? 0, 0), 100)
  return pricePerSession * (1 - pct / 100)
}

/** Sessions → derived minutes + amount, using the student's settings. */
export function fromSessions(
  sessions: number,
  pricePerSession: number | null | undefined,
  sessionLengthMinutes: number | null | undefined
): { minutes: number; amount: number } {
  const s = Number.isFinite(sessions) ? sessions : 0
  return {
    minutes: Math.round(s * getSessionLength(sessionLengthMinutes)),
    amount: pricePerSession != null ? Number((s * pricePerSession).toFixed(2)) : 0,
  }
}

/** Amount → derived sessions + minutes. Returns null sessions when no price set. */
export function fromAmount(
  amount: number,
  pricePerSession: number | null | undefined,
  sessionLengthMinutes: number | null | undefined
): { sessions: number | null; minutes: number } {
  if (!pricePerSession || pricePerSession <= 0) return { sessions: null, minutes: 0 }
  const sessions = (Number.isFinite(amount) ? amount : 0) / pricePerSession
  return {
    sessions: Number(sessions.toFixed(2)),
    minutes: Math.round(sessions * getSessionLength(sessionLengthMinutes)),
  }
}
